import * as React from 'react';
import { echoService } from '@/services/echoService';
import { useAuth } from '@/hooks/useAuth';
import type { UploadedFile } from '@/types/echo_creation';
import type { VoiceCloneMeta, VoiceCloneStatus, VoiceFileSelectHandler } from '../types';

interface UseVoiceCloneOptions {
  draftId: string;
  trainingDone: boolean;
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
}

export function useVoiceClone({ draftId, trainingDone, setFiles }: UseVoiceCloneOptions) {
  const { refreshProfile } = useAuth();

  const [pendingVoiceFile, setPendingVoiceFile] = React.useState<File | null>(null);
  const [pendingVoiceMeta, setPendingVoiceMeta] = React.useState<VoiceCloneMeta | null>(null);
  const [status, setStatus] = React.useState<VoiceCloneStatus>('idle');
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);

  const isFullyDone = trainingDone && (!pendingVoiceFile || status === 'done');
  const hasError = status === 'error';

  const handleFileSelect: VoiceFileSelectHandler = React.useCallback(
    (file, language, accent) => {
      setPendingVoiceFile(file);
      setPendingVoiceMeta({ language, accent });

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      const newBlobUrl = URL.createObjectURL(file);
      blobUrlRef.current = newBlobUrl;

      const voiceFile: UploadedFile = {
        id: `voice-clone-${Date.now()}`,
        name: file.name,
        filename: file.name,
        size: file.size,
        type: file.type || 'audio/webm',
        url: newBlobUrl,
        processing_status: 'completed',
        status: 'completed',
        progress: 100,
      };
      setFiles((prev) => [
        ...prev.filter((existing) => !existing.id.startsWith('voice-clone-')),
        voiceFile,
      ]);

      setUploadError(null);
      setStatus('idle');
    },
    [setFiles]
  );

  const resetState = React.useCallback(() => {
    setStatus('idle');
    setUploadError(null);
  }, []);

  // Revoke blob URL on unmount
  React.useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    },
    []
  );

  // Auto-upload voice clone after training completes
  React.useEffect(() => {
    if (!trainingDone || !pendingVoiceFile || status !== 'idle') return;

    const uploadVoice = async () => {
      setStatus('uploading');
      try {
        await echoService.uploadVoiceClone(
          draftId,
          pendingVoiceFile,
          undefined,
          pendingVoiceMeta?.language,
          pendingVoiceMeta?.accent
        );
        setStatus('done');
        await refreshProfile();
      } catch (err) {
        setStatus('error');
        setUploadError(err instanceof Error ? err.message : 'Voice clone upload failed');
      }
    };

    uploadVoice();
  }, [
    trainingDone,
    pendingVoiceFile,
    status,
    draftId,
    refreshProfile,
    pendingVoiceMeta?.language,
    pendingVoiceMeta?.accent,
  ]);

  return {
    pendingVoiceFile,
    status,
    uploadError,
    isFullyDone,
    hasError,
    handleFileSelect,
    resetState,
  };
}
